/*
 * ⌘K — adapted from Beautiful UI's SearchList (MIT, © 2026 Shane Levine): one field,
 * grouped results, a gliding highlight driven by the arrow keys. Jumps to sections,
 * runs commands, and searches ads and campaigns through the API.
 */
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { endpoints } from "../api/endpoints";
import { I, type IconName } from "../components/tk";
import { useDebounced } from "../lib/hooks";
import { setThemePref, useColorScheme } from "../lib/tg";
import { NAV } from "./nav";
import { useSession, wk } from "./session";

type Item = { id: string; group: string; label: string; sub?: string; icon: IconName; run: () => void; keywords?: string };

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { ws, membership } = useSession();
  const scheme = useColorScheme();
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dq = useDebounced(q.trim(), 180);

  useEffect(() => {
    if (open) {
      setQ("");
      setIdx(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const ads = useQuery({ queryKey: wk(ws, "palette-ads", dq), queryFn: () => endpoints.ads(ws, { q: dq, limit: 5 }), enabled: open && dq.length > 1 });
  const camps = useQuery({ queryKey: wk(ws, "campaigns", {}), queryFn: () => endpoints.campaigns(ws), enabled: open });

  const go = (to: string) => () => {
    navigate(to);
    onClose();
  };
  const items = useMemo<Item[]>(() => {
    const staff = membership.role !== "client_viewer";
    const nav: Item[] = NAV.filter((n) => !(n.staff && !staff)).map((n) => ({ id: `nav-${n.key}`, group: "Go to", label: n.label, icon: n.icon, run: go(n.to), keywords: n.keywords }));
    const cmds: Item[] = [
      ...(staff
        ? [
            { id: "c-new", group: "Actions", label: "New campaign", icon: "plus" as IconName, run: go("/campaigns/new"), keywords: "create launch wizard" },
            { id: "c-rule", group: "Actions", label: "New rule", icon: "flow" as IconName, run: go("/rules/new"), keywords: "automation" },
            { id: "c-audit", group: "Actions", label: "Run account audit", icon: "shield" as IconName, run: go("/agents?tab=findings&audit=1"), keywords: "waste auditor" },
            { id: "c-stop", group: "Actions", label: "Stop automation / Emergency pause", icon: "stop" as IconName, run: go("/settings/controls"), keywords: "kill switch emergency pause all" },
            { id: "c-undo", group: "Actions", label: "Action log & undo", icon: "undo" as IconName, run: go("/settings/actions"), keywords: "history revert" },
          ]
        : []),
      {
        id: "c-theme",
        group: "Actions",
        label: scheme === "dark" ? "Switch to light theme" : "Switch to dark theme",
        icon: scheme === "dark" ? "sun" : "moon",
        run: () => {
          setThemePref(scheme === "dark" ? "light" : "dark");
          onClose();
        },
        keywords: "theme dark light appearance",
      },
    ];
    const campaigns: Item[] = (camps.data?.items ?? []).map((c) => ({ id: `cmp-${c.id}`, group: "Campaigns", label: c.name, sub: `${c.status} · autopilot ${c.autopilot_mode}`, icon: "target", run: go(`/campaigns/${c.id}`) }));
    const adItems: Item[] = (ads.data?.items ?? []).map((a) => ({ id: `ad-${a.id}`, group: "Ads", label: a.title, sub: a.target_label, icon: "megaphone", run: go(`/ads/${a.id}`) }));
    const s = q.trim().toLowerCase();
    const match = (i: Item) => !s || `${i.label} ${i.sub ?? ""} ${i.keywords ?? ""}`.toLowerCase().includes(s);
    return [...nav.filter(match), ...cmds.filter(match), ...campaigns.filter(match), ...adItems];
  }, [q, camps.data, ads.data, scheme, membership.role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => setIdx(0), [q]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${idx}"]`)?.scrollIntoView({ block: "nearest" });
  }, [idx]);

  if (!open) return null;
  let last = "";
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-[var(--scrim)] px-3 pt-[12vh]" onMouseDown={(e) => e.target === e.currentTarget && onClose()} style={{ animation: "fade-in 150ms ease-out both" }}>
      <div role="dialog" aria-modal="true" aria-label="Search and commands" className="w-full max-w-[560px] overflow-hidden rounded-window bg-surface shadow-overlay" style={{ animation: "pop-in 180ms cubic-bezier(0.23,1,0.32,1) both" }}>
        <div className="flex items-center gap-2.5 border-b border-line px-4 h-13">
          <I.search width={18} height={18} className="text-ink-3 flex-none" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search sections, campaigns, ads, commands…"
            className="h-12 flex-1 bg-transparent text-[15px] outline-none placeholder:text-ink-3"
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-activedescendant={items[idx] ? `pal-${items[idx]!.id}` : undefined}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setIdx((i) => Math.min(items.length - 1, i + 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setIdx((i) => Math.max(0, i - 1));
              } else if (e.key === "Enter") {
                e.preventDefault();
                items[idx]?.run();
              } else if (e.key === "Escape") onClose();
            }}
          />
          <kbd className="rounded-[5px] bg-field px-1.5 py-0.5 text-[11px] text-ink-3">esc</kbd>
        </div>
        <div ref={listRef} id="palette-list" role="listbox" className="max-h-[52vh] overflow-y-auto p-1.5">
          {items.length === 0 && <p className="m-0 px-3 py-6 text-center text-[13px] text-ink-3">{ads.isFetching ? "Searching…" : "No results"}</p>}
          {items.map((it, i) => {
            const Icon = I[it.icon];
            const header = it.group !== last ? it.group : null;
            last = it.group;
            return (
              <div key={it.id}>
                {header && <div className="px-2.5 pt-2.5 pb-1 text-[11px] font-medium text-ink-3">{header}</div>}
                <div
                  id={`pal-${it.id}`}
                  data-idx={i}
                  role="option"
                  aria-selected={i === idx}
                  onMouseMove={() => setIdx(i)}
                  onClick={it.run}
                  className={clsx("flex h-10 cursor-pointer items-center gap-3 rounded-control px-2.5 transition-colors duration-75", i === idx ? "bg-accent text-[var(--tk-accent-text)]" : "text-ink")}
                >
                  <Icon width={17} height={17} className={clsx("flex-none", i === idx ? "" : "text-ink-3")} />
                  <span className="min-w-0 flex-1 truncate text-[14px]">{it.label}</span>
                  {it.sub && <span className={clsx("max-w-[45%] truncate text-[12px]", i === idx ? "opacity-80" : "text-ink-3")}>{it.sub}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
